import { useEffect, useRef, useState, type KeyboardEvent } from 'react';

import { Field, Modal, Notice } from '@/components/Modal';
import {
  useSendLoginDeviceCode,
  useVerifyLoginDevice,
  useVerifyLoginTotp,
  type DeviceCodeSent,
} from '@/lib/auth/session';
import type { DeviceChallenge, DeviceChannelKind, Session } from '@/lib/types';

type Step = 'choose' | 'code';

/** 남은 초. 0 밑으로 내려가지 않는다 — PasswordResetModal 과 같은 계산이다. */
function useSecondsLeft(deadline: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (deadline === null) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [deadline]);
  return deadline === null ? 0 : Math.max(0, Math.ceil((deadline - now) / 1000));
}

function formatSeconds(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

const CHANNEL_LABEL: Record<DeviceChannelKind, string> = {
  PHONE: '휴대폰 문자(알림톡)',
  EMAIL: '이메일',
};

/** 보내는 갈래(휴대폰·메일)만 남은 답. */
type CodeChallenge = Omit<DeviceChallenge, 'channels'> & {
  channels: { kind: DeviceChannelKind; masked: string }[];
};

type Props = {
  challenge: DeviceChallenge;
  onVerified: (session: Session) => void | Promise<void>;
  onClose: () => void;
};

/** 로그인 OTP 를 켠 사람(V618)이면 서버가 TOTP 하나만 보낸다 — 보낼 것이 없어 6자리 칸으로 바로 간다. */
export function DeviceVerifyModal({ challenge, onVerified, onClose }: Props) {
  if (challenge.channels.some((option) => option.kind === 'TOTP')) {
    return <TotpLoginModal pendingId={challenge.pendingId} onVerified={onVerified} onClose={onClose} />;
  }
  return <DeviceCodeModal challenge={challenge as CodeChallenge} onVerified={onVerified} onClose={onClose} />;
}

function TotpLoginModal({
  pendingId,
  onVerified,
  onClose,
}: {
  pendingId: string;
  onVerified: (session: Session) => void | Promise<void>;
  onClose: () => void;
}) {
  const verify = useVerifyLoginTotp();
  const [code, setCode] = useState('');
  const canConfirm = code.length === 6 && !verify.isPending;

  const confirm = () => {
    if (!canConfirm) return;
    verify.mutate({ pendingId, code }, { onSuccess: (session) => void onVerified(session), onError: () => setCode('') });
  };

  return (
    <Modal
      title="구글 OTP"
      maxWidth="380px"
      onClose={onClose}
      closeDisabled={verify.isPending}
      actions={
        <button onClick={confirm} className="btn-primary" disabled={!canConfirm}>
          {verify.isPending ? '확인 중...' : '확인'}
        </button>
      }
    >
      <Field label="6자리">
        <input
          className="input-field"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
          onKeyDown={(event) => event.key === 'Enter' && !event.nativeEvent.isComposing && confirm()}
          disabled={verify.isPending}
          autoFocus
          style={{ letterSpacing: '0.3em', textAlign: 'center', fontSize: '18px' }}
        />
      </Field>
      {verify.error && <Notice tone="error">{verify.error.message}</Notice>}
    </Modal>
  );
}

/**
 * 새 브라우저에서의 로그인 — 인증번호 한 걸음(DOC-138 · V585).
 *
 * 비밀번호는 이미 맞았다. 서버가 처음 보는 브라우저라 **받을 방법을 고르게** 한다 — 휴대폰(알림톡) 또는 이메일
 * (사용자 지시 2026-10-28: 「모바일 인증이 한계가 생기면 메일로도」). 고르면 그 길로 6자리가 가고, 맞히면 세션이
 * 열리고 이 브라우저는 기억된다. 「다른 방법으로」 는 고르는 칸으로 돌아가고, 「다시 받기」 는 같은 길로 한 번 더다.
 * 길이 하나뿐이면 고르는 칸을 건너뛰고 바로 보낸다 — 고를 것이 없는데 고르라고 하지 않는다.
 */
function DeviceCodeModal({
  challenge,
  onVerified,
  onClose,
}: Omit<Props, 'challenge'> & { challenge: CodeChallenge }) {
  const send = useSendLoginDeviceCode();
  const verify = useVerifyLoginDevice();

  const [step, setStep] = useState<Step>('choose');
  const [channel, setChannel] = useState<DeviceChannelKind | null>(null);
  const [sent, setSent] = useState<DeviceCodeSent | null>(null);
  const [deadline, setDeadline] = useState<number | null>(null);
  const [code, setCode] = useState('');
  const secondsLeft = useSecondsLeft(deadline);

  const pending = send.isPending || verify.isPending;
  const canConfirm = Boolean(sent) && code.trim().length === 6 && secondsLeft > 0 && !pending;

  /*
    보내는 중 잠금. `send.isPending` 은 다음 렌더에서야 참이 되어, 같은 틱에 두 번 눌리면 둘 다 지나간다 —
    2026-10-28 개발계에서 같은 순간에 두 통이 나갔다(두 번째가 첫 번째를 무효로 돌려 화면의 번호가 안 맞는다).
  */
  const sending = useRef(false);
  const sendTo = (kind: DeviceChannelKind) => {
    if (pending || sending.current) return;
    sending.current = true;
    setChannel(kind);
    send.mutate(
      { pendingId: challenge.pendingId, channel: kind },
      {
        onSuccess: (result) => {
          setSent(result);
          setDeadline(Date.now() + result.expiresInSeconds * 1000);
          setCode('');
          verify.reset();
          setStep('code');
        },
        onSettled: () => {
          sending.current = false;
        },
      },
    );
  };

  // 길이 하나뿐이면 고르게 하지 않고 바로 보낸다.
  const only = challenge.channels.length === 1 ? challenge.channels[0] : null;
  useEffect(() => {
    if (only && step === 'choose' && !send.isPending && !sent) sendTo(only.kind);
    // sendTo 는 매 렌더 새 손잡이라 의존성에 넣으면 매번 돈다 — 처음 한 번이면 된다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [only?.kind]);

  const confirm = () => {
    if (!canConfirm || !sent) return;
    verify.mutate(
      { pendingId: challenge.pendingId, verificationId: sent.verificationId, code: code.trim() },
      { onSuccess: (session) => void onVerified(session) },
    );
  };

  const onEnter = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) confirm();
  };

  const error = step === 'choose' ? send.error : (verify.error ?? send.error);

  return (
    <Modal
      title="본인 확인"
      maxWidth="440px"
      onClose={onClose}
      closeDisabled={pending}
      footer={
        <>
          <button onClick={onClose} className="btn-secondary" disabled={pending}>
            취소
          </button>
          {step === 'code' && (
            <>
              {challenge.channels.length > 1 && (
                <button
                  onClick={() => {
                    setStep('choose');
                    send.reset();
                  }}
                  className="btn-secondary"
                  disabled={pending}
                >
                  다른 방법으로
                </button>
              )}
              <button onClick={() => channel && sendTo(channel)} className="btn-secondary" disabled={pending || !channel}>
                {send.isPending ? '보내는 중...' : '다시 받기'}
              </button>
              <button onClick={confirm} className="btn-primary" disabled={!canConfirm}>
                {verify.isPending ? '확인 중...' : '확인'}
              </button>
            </>
          )}
        </>
      }
    >
      <p className="text-sm" style={{ color: 'var(--text-secondary)', marginBottom: '12px', wordBreak: 'keep-all' }}>
        처음 로그인하는 브라우저입니다. 인증번호를 받아 본인임을 확인해 주세요.
      </p>

      {step === 'choose' && (
        <div className="space-y-2">
          {challenge.channels.map((option) => (
            <button
              key={option.kind}
              onClick={() => sendTo(option.kind)}
              className="btn-secondary"
              style={{ width: '100%', justifyContent: 'space-between', display: 'flex' }}
              disabled={pending}
            >
              <span>{send.isPending && channel === option.kind ? '보내는 중...' : `${CHANNEL_LABEL[option.kind]}로 받기`}</span>
              <span style={{ color: 'var(--text-muted)' }}>{option.masked}</span>
            </button>
          ))}
        </div>
      )}

      {step === 'code' && sent && (
        <>
          <Field label={`인증번호 · ${CHANNEL_LABEL[sent.channel] ?? sent.channel} ${sent.maskedTarget} · ${formatSeconds(secondsLeft)}`}>
            <input
              className="input-field"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
              onKeyDown={onEnter}
              disabled={pending}
              autoFocus
            />
          </Field>
          {sent.channel === 'EMAIL' && (
            <p className="text-xs" style={{ color: 'var(--text-muted)', marginTop: '6px' }}>
              메일은 1분쯤 걸릴 수 있습니다. 스팸함도 확인해 주세요.
            </p>
          )}
          {secondsLeft === 0 && !pending && (
            <Notice tone="warning">유효시간이 지났습니다. [다시 받기] 를 눌러 주세요.</Notice>
          )}
        </>
      )}

      {error && <Notice tone="error">{error.message}</Notice>}
    </Modal>
  );
}
